/**
 * Scroll targets for Schedule Interview communication step.
 * Section ids match EmailBlockSection `sectionId` in CandidatePane / InterviewerPane.
 */
import type { InterviewCommunicationValidationErrors } from '../../../InterviewActivity/communication/validate';

export type ScheduleCommunicationSectionKey =
  keyof InterviewCommunicationValidationErrors;

export type ScheduleCommunicationScrollTarget = {
  key: ScheduleCommunicationSectionKey;
  sectionId: string;
};

const SCHEDULE_ERROR_SCROLL_ORDER: Array<{
  key: ScheduleCommunicationSectionKey;
  sectionId: string;
}> = [
  { key: 'candidateCalendar', sectionId: 'schedule-comm-candidate-calendar' },
  { key: 'candidateEmail', sectionId: 'schedule-comm-candidate-email' },
  {
    key: 'interviewerCalendar',
    sectionId: 'schedule-comm-interviewer-calendar',
  },
  { key: 'interviewerEmail', sectionId: 'schedule-comm-interviewer-email' },
  { key: 'selfSchedule', sectionId: 'schedule-comm-self-schedule' },
];

export function getFirstScheduleCommunicationScrollTarget(
  errors: InterviewCommunicationValidationErrors
): ScheduleCommunicationScrollTarget | null {
  for (const { key, sectionId } of SCHEDULE_ERROR_SCROLL_ORDER) {
    if (errors[key]) {
      return { key, sectionId };
    }
  }
  return null;
}

export function scrollScheduleCommunicationSectionIntoView(
  sectionId: string
): void {
  const el = document.getElementById(sectionId);
  if (!el) return;

  // Find the nearest overflow-auto/scroll ancestor so we scroll within
  // the communication step's own container, not the window. Using
  // scrollIntoView({ block: 'start' }) escalates to outer containers
  // when the element is near the bottom, which shifts the footer.
  const scrollParent = getScrollParent(el);
  if (!scrollParent) {
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return;
  }

  const parentRect = scrollParent.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const targetScrollTop =
    scrollParent.scrollTop + (elRect.top - parentRect.top) - 16;
  scrollParent.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
}

function getScrollParent(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const { overflow, overflowY } = window.getComputedStyle(node);
    if (/auto|scroll/.test(overflow) || /auto|scroll/.test(overflowY)) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}
